import React, { useContext, useState } from "react";
import "./CartItems.css";
import cross_icon from "../Assets/cart_cross_icon.png";
import { MdDelete } from "react-icons/md";
import { ShopContext } from "../../Context/ShopContext";
import { backend_url, currency } from "../../App";
import { parseCartKey, findColorInProduct } from "../../utils/cartKey";
import OrderWithCartsPopup from "../OrderPopUp/OrderWithCartsPopup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FREE_SHIPPING_THRESHOLD, SHIPPING_ZONES, getOrderCharges } from "../../utils/shipping";

const minShippingFee = Math.min(...Object.values(SHIPPING_ZONES).map((z) => z.fee));

const unitPrice = (product) =>
  (product.old_price * (100 - product.new_price)) / 100;

const CartItems = () => {
  const {
    products,
    cartItems,
    addToCart,
    removeFromCart,
    deleteFromCart,
    getTotalCartAmount,
    getTotalCartItems,
  } = useContext(ShopContext);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  const entries = Object.keys(cartItems)
    .filter((key) => cartItems[key] > 0)
    .map((key) => {
      const { productId, colorId } = parseCartKey(key);
      const product = products.find((p) => p.id === productId);
      if (!product) return null;
      const color = findColorInProduct(product, colorId);
      const image = color?.images?.[0] || product.images?.[0] || product.image;
      return { key, product, color, image, quantity: cartItems[key] };
    })
    .filter(Boolean);

  const subtotal = getTotalCartAmount();
  // No address yet — shipping is priced by location at checkout.
  const { gst, total, pending: shippingPending } = getOrderCharges(subtotal);

  const openCheckout = () => {
    if (!localStorage.getItem("auth-token")) {
      toast.info("Please Login/Register to continue with your order");
      navigate("/login", { state: { from: window.location.pathname } });
      return;
    }
    setShowPopup(true);
  };

  return (
    <div className="cartitems">
      {/* Desktop / tablet table layout — unchanged */}
      <div className="hidden md:block">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Your Cart {getTotalCartItems() > 0 && `(${getTotalCartItems()})`}
        </h1>
        <div className="cartitems-format-main">
          <p>Products</p>
          <p>Title</p>
          <p>Price</p>
          <p>Quantity</p>
          <p>Total</p>
          <p>Remove</p>
        </div>
        <hr />
        {entries.map(({ key, product: e, color, image, quantity }) => (
          <div key={key}>
            <div className="cartitems-format-main cartitems-format">
              <img crossOrigin="anonymous" className="cartitems-product-icon" src={image?.startsWith("http") ? image : backend_url + image} alt="" />
              <p cartitems-product-title="">{e.name}{color ? ` — ${color.name}` : ""}</p>
              <p>{currency}{unitPrice(e).toFixed(2)}</p>
              <button className="cartitems-quantity">{quantity}</button>
              <p>{currency}{(unitPrice(e) * quantity).toFixed(2)}</p>
              <img crossOrigin="anonymous" onClick={() => { removeFromCart(key) }} className="cartitems-remove-icon" src={cross_icon} alt="" />
            </div>
            <hr />
          </div>
        ))}

        <div className="cartitems-down">
          <div className="cartitems-total">
            <h1>Cart Totals</h1>
            <div>
              <div className="cartitems-total-item">
                <p>Products</p>
                <p>{getTotalCartItems()}</p>
              </div>
              <hr />
              <div className="cartitems-total-item">
                <p>Subtotal</p>
                <p>{currency}{subtotal.toFixed(2)}</p>
              </div>
              <hr />
              <div className="cartitems-total-item">
                <p>Shipping Fee</p>
                <p>{shippingPending ? `From ${currency}${minShippingFee} · at checkout` : "Free"}</p>
              </div>
              <hr />
              <div className="cartitems-total-item">
                <p>GST (5%)</p>
                <p>{currency}{gst.toFixed(2)}</p>
              </div>
              <hr />
              <div className="cartitems-total-item">
                <h3>{shippingPending ? "Total (excl. shipping)" : "Total"}</h3>
                <h3>{currency}{total.toFixed(2)}</h3>
              </div>
            </div>
            {shippingPending && (
              <p className="text-xs text-gray-500 mt-2">
                Add {currency}{(FREE_SHIPPING_THRESHOLD - subtotal).toFixed(2)} more to get free shipping.
              </p>
            )}
            <button onClick={openCheckout}>PROCEED TO CHECKOUT</button>
          </div>
          <div className="cartitems-promocode">
            <p>If you have a promo code, Enter it here</p>
            <div className="cartitems-promobox">
              <input type="text" placeholder="promo code" />
              <button>Submit</button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden px-4 pt-4 pb-28">
        <h1 className="text-lg font-bold text-gray-800 mb-3">
          Your Cart {getTotalCartItems() > 0 && `(${getTotalCartItems()})`}
        </h1>

        {entries.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Your cart is empty</div>
        ) : (
          <div className="flex flex-col gap-3">
            {entries.map(({ key, product, color, image, quantity }) => {
              const step = product.scale?.toLowerCase() === "meters" ? 0.5 : 1;
              const src = image?.startsWith("http") ? image : backend_url + image;
              return (
                <div key={key} className="flex gap-3 bg-white rounded-2xl border border-[var(--brand-primary-100)] shadow-sm p-3">
                  <img crossOrigin="anonymous" src={src} alt={product.name} className="w-20 h-20 object-cover rounded-xl border border-[var(--brand-primary-100)] flex-shrink-0" />
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-800 truncate">{product.name}</p>
                      {color && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color.hex, border: "1px solid rgba(0,0,0,0.15)" }} />
                          {color.name}
                        </span>
                      )}
                      <p className="text-sm text-[var(--brand-primary-700)] font-semibold mt-1">
                        {currency}{(unitPrice(product) * quantity).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center border border-[var(--brand-primary-200)] rounded-lg overflow-hidden">
                        <button className="px-2.5 py-1 text-sm" onClick={() => removeFromCart(key, step)}>-</button>
                        <span className="px-2 text-sm min-w-[28px] text-center">{quantity}</span>
                        <button className="px-2.5 py-1 text-sm" onClick={() => addToCart(key, step)}>+</button>
                      </div>
                      <button onClick={() => deleteFromCart(key)} className="p-1.5 text-red-500">
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky checkout bar — mobile only, floats above the bottom tab bar
          with a visible gap so the two don't read as one fused strip. Always
          shown while the cart has items — this is the primary action, so it
          must never be hidden by a footer-proximity heuristic. */}
      {entries.length > 0 && (
        <div
          className="md:hidden fixed inset-x-3 z-20 bg-white rounded-2xl border border-[var(--brand-primary-100)] shadow-[0_4px_20px_rgba(124,58,237,0.18)] px-4 py-3 flex items-center justify-between gap-3"
          style={{ bottom: "calc(64px + 12px + env(safe-area-inset-bottom))" }}
        >
          <div>
            <p className="text-[11px] text-gray-500 leading-tight">
              Total · incl. GST · {shippingPending ? "shipping at checkout" : "free shipping"}
            </p>
            <p className="text-base font-bold text-gray-800 leading-tight">{currency}{total.toFixed(2)}</p>
          </div>
          <button
            onClick={openCheckout}
            className="text-white px-6 py-2.5 rounded-full font-semibold text-sm shadow active:scale-95 transition-transform"
            style={{ background: "linear-gradient(90deg, var(--brand-primary-600), var(--brand-secondary-500))" }}
          >
            Proceed to Checkout
          </button>
        </div>
      )}

      <OrderWithCartsPopup
        cartItems={entries.map(({ product, color, quantity }) => ({
          id: product.id,
          productId: product._id,
          name: product.name,
          quantity,
          price: unitPrice(product),
          image: color?.images?.[0] || product.images?.[0] || "",
          color: color?.name || "",
          colorHex: color?.hex || "",
        }))}
        currency={currency}
        showPopup={showPopup}
        closePopup={() => setShowPopup(false)}
      />
    </div>
  );
};

export default CartItems;
